import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react'

/**
 * Plynule tazeni „Pruzina" (navrh schvaleny 2026-09-13). Drzeny radek je kopie
 * nad strankou: jde za prstem na pruzine, naklani se podle rychlosti pohybu a po
 * pusteni dosedne na skutecne misto radku. V seznamu je behem tahu na jeho
 * miste prohlubeň (CSS body.is-lifting) a ostatni radky dojedou na nove pozice
 * (useFlip). S omezenym pohybem v systemu se nic z toho nedeje a tah zustava
 * postaru.
 */

const FOLLOW = { k: 520, c: 34 }
const LAND = { k: 380, c: 21 }
const LIFT_SCALE = 1.035
const MAX_TILT_DEG = 9
const FLIP_MS = 280
const FLIP_EASE = 'cubic-bezier(0.2, 0.9, 0.25, 1)'
/** Pojistka, kdyby kopie nikdy nedosedla. */
const LAND_TIMEOUT_MS = 1200
/** Starsi zachycene pozice uz k nove zmene poradi nepatri. */
const CAPTURE_TTL_MS = 300

export function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** Pruzina s tlumenim po malych krocich — stabilni i pri pomalem snimku. */
function spring(x: number, v: number, target: number, k: number, c: number, dt: number): [number, number] {
  const steps = Math.ceil(dt / 0.006)
  const h = dt / steps
  let pos = x
  let vel = v
  for (let i = 0; i < steps; i++) {
    vel += (k * (target - pos) - c * vel) * h
    pos += vel * h
  }
  return [pos, vel]
}

/**
 * Pozice radku bez rozbehnute animace uhybani. Podle ni se pocita, kam radek
 * spadne — jinak by se poradi pri kazde animaci prehazovalo tam a zpet.
 */
export function restingRect(el: HTMLElement): DOMRect {
  const rect = el.getBoundingClientRect()
  const transform = getComputedStyle(el).transform
  if (!transform || transform === 'none') return rect
  const m = new DOMMatrixReadOnly(transform)
  return new DOMRect(rect.left - m.m41, rect.top - m.m42, rect.width, rect.height)
}

export interface Lift {
  move(x: number, y: number): void
  /** Pusteni: kopie dosedne na element, ktery vraci target — hleda se kazdy snimek. */
  drop(target: () => HTMLElement | null): void
  /** Hned pryc bez animace (odchod ze stranky, dalsi tah). */
  destroy(): void
}

let activeLifts = 0

/**
 * Zvedne radek. null = omezeny pohyb v systemu.
 * context = trida predka, na ktere zavisi rozmery uvnitr radku (treba „board").
 */
export function liftRow(row: HTMLElement, x: number, y: number, context?: string): Lift | null {
  if (reducedMotion()) return null
  const rect = row.getBoundingClientRect()
  const grabX = x - rect.left
  const grabY = y - rect.top

  const wrap = document.createElement('div')
  wrap.className = context ? `drag-lift ${context}` : 'drag-lift'
  wrap.setAttribute('aria-hidden', 'true')
  wrap.inert = true
  wrap.style.width = `${rect.width}px`
  wrap.style.height = `${rect.height}px`
  wrap.style.transformOrigin = `${grabX}px ${grabY}px`
  wrap.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0)`
  const ghost = row.cloneNode(true) as HTMLElement
  // Kopie nesmi odpovidat dotazum na radky seznamu.
  for (const el of [ghost, ...ghost.querySelectorAll<HTMLElement>('*')]) {
    for (const name of el.getAttributeNames()) {
      if (name.startsWith('data-') || name === 'id') el.removeAttribute(name)
    }
  }
  ghost.classList.add('drag-ghost')
  wrap.appendChild(ghost)
  document.body.appendChild(wrap)
  activeLifts += 1
  document.body.classList.add('is-lifting')

  const now = performance.now()
  const s = {
    px: x,
    py: y,
    lastX: x,
    lastMove: now,
    pvx: 0,
    x: rect.left,
    y: rect.top,
    vx: 0,
    vy: 0,
    scale: 1,
    vscale: 0,
    rot: 0,
    vrot: 0,
    t: now,
    landing: false,
    landStarted: 0,
  }
  let target: (() => HTMLElement | null) | null = null
  let landingEl: HTMLElement | null = null
  let raf = 0
  let done = false

  const markLanding = (el: HTMLElement | null) => {
    if (landingEl === el) return
    landingEl?.removeAttribute('data-lift-landing')
    el?.setAttribute('data-lift-landing', '')
    landingEl = el
  }

  const finish = (settle: boolean) => {
    if (done) return
    done = true
    cancelAnimationFrame(raf)
    wrap.remove()
    const landed = landingEl
    markLanding(null)
    activeLifts -= 1
    if (activeLifts === 0) document.body.classList.remove('is-lifting')
    if (settle && landed?.isConnected) {
      landed.classList.remove('lift-settle')
      void landed.offsetWidth
      landed.classList.add('lift-settle')
      landed.addEventListener('animationend', () => landed.classList.remove('lift-settle'), { once: true })
    }
  }

  const tick = (time: number) => {
    const dt = clamp((time - s.t) / 1000, 0.001, 0.034)
    s.t = time
    // Kdyz se ruka zastavi, narovna se i radek.
    if (time - s.lastMove > 40) s.pvx *= Math.pow(0.02, dt)

    let tx = s.px - grabX
    let ty = s.py - grabY
    let tscale = LIFT_SCALE
    let trot = clamp(s.pvx * 0.011, -MAX_TILT_DEG, MAX_TILT_DEG)
    if (s.landing) {
      const el = target?.() ?? null
      markLanding(el)
      if (!el) {
        finish(false)
        return
      }
      const r = el.getBoundingClientRect()
      tx = r.left
      ty = r.top
      tscale = 1
      trot = 0
      wrap.style.width = `${r.width}px`
    }

    const p = s.landing ? LAND : FOLLOW
    ;[s.x, s.vx] = spring(s.x, s.vx, tx, p.k, p.c, dt)
    ;[s.y, s.vy] = spring(s.y, s.vy, ty, p.k, p.c, dt)
    ;[s.scale, s.vscale] = spring(s.scale, s.vscale, tscale, 320, 20, dt)
    ;[s.rot, s.vrot] = spring(s.rot, s.vrot, trot, 260, 17, dt)
    wrap.style.transform = `translate3d(${s.x}px, ${s.y}px, 0) rotate(${s.rot}deg) scale(${s.scale})`

    if (s.landing) {
      const settled =
        Math.abs(s.x - tx) < 0.6 &&
        Math.abs(s.y - ty) < 0.6 &&
        Math.abs(s.vx) + Math.abs(s.vy) < 20 &&
        Math.abs(s.scale - 1) < 0.004 &&
        Math.abs(s.rot) < 0.2
      if (settled || time - s.landStarted > LAND_TIMEOUT_MS) {
        finish(true)
        return
      }
    }
    raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)

  return {
    move(nextX, nextY) {
      if (done || s.landing) return
      const time = performance.now()
      const elapsed = Math.max(8, time - s.lastMove)
      s.pvx = s.pvx * 0.6 + ((nextX - s.lastX) / elapsed) * 1000 * 0.4
      s.px = nextX
      s.py = nextY
      s.lastX = nextX
      s.lastMove = time
    },
    drop(find) {
      if (done) return
      target = find
      s.landing = true
      s.landStarted = performance.now()
      wrap.classList.add('is-landing')
      // Hned, ne az dalsi snimek — jinak by radek pod kopii na okamzik probliknul.
      markLanding(find())
    },
    destroy() {
      finish(false)
    },
  }
}

/**
 * Radky seznamu dojedou z puvodnich pozic na nove (FLIP). capture() zavolat
 * tesne pred zmenou poradi; animace probehne po vykresleni.
 */
export function useFlip(ref: RefObject<HTMLElement | null>, selector: string, keyAttr: string): () => void {
  const before = useRef<{ at: number; rects: Map<string, DOMRect> } | null>(null)

  useLayoutEffect(() => {
    const captured = before.current
    const root = ref.current
    before.current = null
    if (!captured || !root || performance.now() - captured.at > CAPTURE_TTL_MS || reducedMotion()) return
    for (const el of root.querySelectorAll<HTMLElement>(selector)) {
      const key = el.getAttribute(keyAttr)
      const from = key ? captured.rects.get(key) : undefined
      if (!from) continue
      el.getAnimations().forEach((animation) => animation.cancel())
      const to = el.getBoundingClientRect()
      const dx = from.left - to.left
      const dy = from.top - to.top
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
        duration: FLIP_MS,
        easing: FLIP_EASE,
      })
    }
  })

  return useCallback(() => {
    const root = ref.current
    if (!root) return
    const rects = new Map<string, DOMRect>()
    for (const el of root.querySelectorAll<HTMLElement>(selector)) {
      const key = el.getAttribute(keyAttr)
      if (key) rects.set(key, el.getBoundingClientRect())
    }
    before.current = { at: performance.now(), rects }
  }, [ref, selector, keyAttr])
}
